import { useParams } from "react-router-dom";
import {
  useCancelTicketMutation,
  useCloseTicketMutation,
  useGetTicketLogDetailsQuery,
  useReopenTicketMutation,
} from "../../../../../features/superAdmin/ticketService/ticket/ticket.api";
import SidebarLayout from "../../../../../layouts/SidebarLayout";

import { Button, Input } from "antd";
import { useEffect, useState } from "react";
import ActionModal from "../../../../../components/ui/ActionModal";
import { TicketDetails as TicketDetailsType } from "../../../../../features/superAdmin/ticketService/ticket/ticket.type";
import useError from "../../../../../hooks/useError";
import useModal from "../../../../../hooks/useModal";
import useTicket from "../../../../../hooks/useTicket";
import { toast } from "../../../../../lib/toast";
import { TEMPLATE_TYPES } from "../../constants/ticket.constants";
import DetailsSpecification from "./_components/DetailsSpecification";
import ForwardOpenTicketModal from "./_components/ForwardOpenTicketModal";
import ForwardWorkflowTicketModal from "./_components/ForwardWorkflowTicketModal";
import TicketDetailsHeader from "./_components/TicketDetailsHeader";
import TicketDetailsTabs from "./_components/TicketDetailsTabs";
import TicketDetailsTitleDesc from "./_components/TicketDetailsTitleDesc";

const ACTION_CONFIG = {
  close: "Close",
  cancel: "Cancel",
  reopen: "Re-Open",
};
const CreatorTicketDetails = () => {
  const { id } = useParams();

  const { open, handleOpen, handleClose } = useModal<
    { action: string },
    "CLOSE" | "CANCEL" | "FORWARD" | "RE-OPEN"
  >();
  // Component Status
  const [remark, setRemark] = useState<string>("");

  const { data: detailsResponse } = useGetTicketLogDetailsQuery({
    params: { id: id as string, emp_id: "" },
  });
  const ticketDetails = (detailsResponse?.data || {}) as TicketDetailsType;

  const selectedRole = open.data?.action;
  const roleLabel = ACTION_CONFIG?.[selectedRole as keyof typeof ACTION_CONFIG];
  const { canReopenTicket, canPerformAction } = useTicket(ticketDetails);
  const [
    cancelTicket,
    {
      isSuccess: isCanceled,
      isError: isCancelError,
      error: cancelError,
      isLoading: canceling,
    },
  ] = useCancelTicketMutation();

  const [
    closeTicket,
    {
      isSuccess: isClosed,
      isError: isCloseError,
      error: closeError,
      isLoading: closing,
    },
  ] = useCloseTicketMutation();
  const [
    reopenTicket,
    {
      isLoading: reopening,
      isError: isReopenError,
      error: reopenError,
      isSuccess: isReopened,
    },
  ] = useReopenTicketMutation();

  const handleAction = () => {
    if (selectedRole === "reopen") {
      reopenTicket({ id: ticketDetails?.id });
      return;
    }
    if (!remark || remark === "") {
      toast("error", "Please add a comment to proceed.");
      return;
    }

    if (selectedRole === "close") {
      closeTicket({
        id: ticketDetails?.id,
        data: { remark },
      });
    } else if (selectedRole === "cancel") {
      cancelTicket({
        id: ticketDetails?.id,
        data: { remark },
      });
    }
  };

  useEffect(() => {
    if (isCanceled) {
      handleClose();
      setRemark("");
      toast("success", "Ticket canceled successfully!");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCanceled]);

  useEffect(() => {
    if (isClosed) {
      handleClose();
      setRemark("");
      toast("success", "Ticket closed successfully!");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClosed]);

  useEffect(() => {
    if (isReopened) {
      handleClose();
      setRemark("");
      toast("success", "Ticket re-opened successfully!");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReopened]);

  // Errors
  useError(isCloseError, closeError, "Ticket close failed.");
  useError(isCancelError, cancelError, "Ticket cancel failed.");
  useError(isReopenError, reopenError, "Ticket re-open failed.");

  return (
    <SidebarLayout title="Ticket Management">
      <TicketDetailsHeader>
        <div className="flex items-center gap-4">
          {!!canPerformAction && (
            <>
              <Button
                onClick={() => {
                  handleOpen({ type: "CANCEL", data: { action: "cancel" } });
                }}
                className="button-primary-light h-10"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  handleOpen({ type: "CLOSE", data: { action: "close" } });
                }}
                className="button-primary-light h-10"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  handleOpen({ type: "FORWARD", data: { action: "forward" } });
                }}
                className="button-primary-light h-10"
              >
                Forward
              </Button>
            </>
          )}
          {!!canReopenTicket && (
            <Button
              onClick={() => {
                handleOpen({ type: "RE-OPEN", data: { action: "reopen" } });
              }}
              className="button-primary-light h-10"
            >
              Re-Open
            </Button>
          )}
        </div>
      </TicketDetailsHeader>
      <div className="grid grid-cols-5 gap-4">
        <div className="col-span-3">
          <TicketDetailsTitleDesc ticket={ticketDetails} />
          <DetailsSpecification ticket={ticketDetails} />
        </div>
        <div className="col-span-2">
          <TicketDetailsTabs ticketDetails={ticketDetails} />
        </div>
      </div>
      {!!(
        open.type === "CLOSE" ||
        open.type === "CANCEL" ||
        open.type === "RE-OPEN"
      ) && (
        <ActionModal
          cancelText="No"
          open={open?.open}
          handleClose={handleClose}
          handleCancel={handleClose}
          handleAction={handleAction}
          actionBtnIconType="refresh"
          cancelButton={selectedRole !== "reopen"}
          actionBtnIcon={selectedRole === "reopen"}
          loading={closing || canceling || reopening}
          title={`Do you want to ${roleLabel} the ticket?`}
          iconType={selectedRole === "cancel" ? "danger" : "warning"}
          actionBtnText={open.type === "RE-OPEN" ? "Re-Open" : "Yes"}
          subTitle={`Are you sure you want to ${roleLabel} this ticket? This action cannot be undone.`}
        >
          {!!(selectedRole !== "reopen") && (
            <Input
              className="h-10"
              type="text"
              placeholder="Add comment"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />
          )}
        </ActionModal>
      )}

      <ForwardWorkflowTicketModal
        open={
          open.open &&
          open.type === "FORWARD" &&
          ticketDetails.type === TEMPLATE_TYPES.workflow
        }
        handleClose={handleClose}
        id={ticketDetails?.id}
      />
      <ForwardOpenTicketModal
        open={
          open.open &&
          open.type === "FORWARD" &&
          ticketDetails.type === TEMPLATE_TYPES.sla
        }
        handleClose={handleClose}
        id={ticketDetails?.id}
      />
    </SidebarLayout>
  );
};

export default CreatorTicketDetails;
